import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  School,
  Quiz,
  Assessment,
  People,
  Timer,
  EmojiEvents,
  Refresh,
  Analytics,
  Subject,
  Grade,
  BarChart,
  Timeline,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { adminService, DashboardStats } from '../services/adminService';

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const MetricCard = styled(Card)<{ cardcolor: string }>`
  background: linear-gradient(135deg, ${props => props.cardcolor}15 0%, ${props => props.cardcolor}05 100%);
  border: 1px solid ${props => props.cardcolor}1A;
  transition: all 0.3s ease;
  height: 100%;
  min-height: 160px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px ${props => props.cardcolor}26;
  }
`;

const getSubjectColor = (subject: string) => {
  const colors: { [key: string]: string } = {
    math: '#2196f3',
    science: '#4caf50',
    english: '#ff9800',
    gk: '#9c27b0',
    default: '#757575',
  };
  return colors[subject.toLowerCase()] || colors.default;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const AdminAnalytics: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getDashboardStats();
      setStats(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading analytics...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="info">
          No analytics data available
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Key Metrics Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        <MetricCard cardcolor="#1976d2">
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                    {stats.overview.totalStudents}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Total Students
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active learners
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                    +{stats.growth.studentsWeek} this week
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#1976d226', color: '#1976d2' }}>
                <People />
              </Box>
            </Box>
          </CardContent>
        </MetricCard>

        <MetricCard cardcolor="#2e7d32">
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}>
                    {stats.overview.totalTests}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Total Tests
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    All time
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <TrendingUp sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                    +{stats.growth.testsWeek} this week
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#2e7d3226', color: '#2e7d32' }}>
                <Assessment />
              </Box>
            </Box>
          </CardContent>
        </MetricCard>

        <MetricCard cardcolor="#ed6c02">
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ed6c02', mb: 1 }}>
                    {stats.overview.totalQuestions}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Total Questions
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Across all subjects
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#ed6c0226', color: '#ed6c02' }}>
                <Quiz />
              </Box>
            </Box>
          </CardContent>
        </MetricCard>

        <MetricCard cardcolor="#9c27b0">
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 1 }}>
                    {stats.overview.avgScore.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Average Score
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {stats.overview.avgAccuracy.toFixed(1)}% accuracy
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#9c27b026', color: '#9c27b0' }}>
                <TrendingUp />
              </Box>
            </Box>
          </CardContent>
        </MetricCard>
      </Box>

      {/* Analytics Tabs */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              icon={<BarChart />} 
              label="Performance" 
              id="analytics-tab-0"
              aria-controls="analytics-tabpanel-0"
            />
            <Tab 
              icon={<Subject />} 
              label="Subjects" 
              id="analytics-tab-1"
              aria-controls="analytics-tabpanel-1"
            />
            <Tab 
              icon={<Timeline />} 
              label="Activity" 
              id="analytics-tab-2"
              aria-controls="analytics-tabpanel-2"
            />
            <Tab 
              icon={<EmojiEvents />} 
              label="Leaderboard" 
              id="analytics-tab-3"
              aria-controls="analytics-tabpanel-3"
            />
          </Tabs>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchAnalytics}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Performance Analytics */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
          {/* Growth Metrics */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Growth Metrics
              </Typography>
                             <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                 <Box sx={{ 
                   p: 2, 
                   borderRadius: 2, 
                   background: 'linear-gradient(135deg, #1976d215 0%, #1976d205 100%)',
                   border: '1px solid #1976d21A',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'space-between'
                 }}>
                   <Box>
                     <Typography variant="h5" fontWeight={600} sx={{ color: '#1976d2' }}>
                       {stats.growth.studentsToday}
                     </Typography>
                     <Typography variant="body2" color="textSecondary">Students Today</Typography>
                   </Box>
                   <Box sx={{ p: 1, borderRadius: 1, backgroundColor: '#1976d226', color: '#1976d2' }}>
                     <People sx={{ fontSize: 20 }} />
                   </Box>
                 </Box>
                 <Box sx={{ 
                   p: 2, 
                   borderRadius: 2, 
                   background: 'linear-gradient(135deg, #2e7d3215 0%, #2e7d3205 100%)',
                   border: '1px solid #2e7d321A',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'space-between'
                 }}>
                   <Box>
                     <Typography variant="h5" fontWeight={600} sx={{ color: '#2e7d32' }}>
                       {stats.growth.testsToday}
                     </Typography>
                     <Typography variant="body2" color="textSecondary">Tests Today</Typography>
                   </Box>
                   <Box sx={{ p: 1, borderRadius: 1, backgroundColor: '#2e7d3226', color: '#2e7d32' }}>
                     <Assessment sx={{ fontSize: 20 }} />
                   </Box>
                 </Box>
                 <Box sx={{ 
                   p: 2, 
                   borderRadius: 2, 
                   background: 'linear-gradient(135deg, #ed6c0215 0%, #ed6c0205 100%)',
                   border: '1px solid #ed6c021A',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'space-between'
                 }}>
                   <Box>
                     <Typography variant="h5" fontWeight={600} sx={{ color: '#ed6c02' }}>
                       {stats.growth.studentsMonth}
                     </Typography>
                     <Typography variant="body2" color="textSecondary">Students This Month</Typography>
                   </Box>
                   <Box sx={{ p: 1, borderRadius: 1, backgroundColor: '#ed6c0226', color: '#ed6c02' }}>
                     <People sx={{ fontSize: 20 }} />
                   </Box>
                 </Box>
                 <Box sx={{ 
                   p: 2, 
                   borderRadius: 2, 
                   background: 'linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)',
                   border: '1px solid #9c27b01A',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'space-between'
                 }}>
                   <Box>
                     <Typography variant="h5" fontWeight={600} sx={{ color: '#9c27b0' }}>
                       {stats.growth.testsMonth}
                     </Typography>
                     <Typography variant="body2" color="textSecondary">Tests This Month</Typography>
                   </Box>
                   <Box sx={{ p: 1, borderRadius: 1, backgroundColor: '#9c27b026', color: '#9c27b0' }}>
                     <Assessment sx={{ fontSize: 20 }} />
                   </Box>
                 </Box>
               </Box>
            </CardContent>
          </StyledCard>

          {/* Grade Distribution */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Grade Distribution
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {stats.distribution.gradeDistribution.map((grade) => (
                  <Box key={grade._id} sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={500}>
                        Grade {grade._id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {grade.count} students
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(grade.count / stats.overview.totalStudents) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </TabPanel>

      {/* Subject Analytics */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
          {/* Questions by Subject */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Questions by Subject
              </Typography>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {stats.distribution.questionsBySubject.map((subject) => (
                  <Box key={subject._id} sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: getSubjectColor(subject._id),
                          }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {subject._id.charAt(0).toUpperCase() + subject._id.slice(1)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {subject.count} questions
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(subject.count / stats.overview.totalQuestions) * 100}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getSubjectColor(subject._id)
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>

          {/* Popular Subjects */}
          <StyledCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Most Popular Subjects
              </Typography>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {stats.distribution.popularSubjects.map((subject, index) => (
                  <Box key={subject._id} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{ 
                          bgcolor: getSubjectColor(subject._id),
                          color: 'white',
                          fontWeight: 600,
                          minWidth: 35
                        }}
                      />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {subject._id.charAt(0).toUpperCase() + subject._id.slice(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {subject.count} tests taken
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
        </Box>
      </TabPanel>

      {/* Activity Feed */}
      <TabPanel value={tabValue} index={2}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Recent Test Activity
            </Typography>
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {stats.activity.recentTests.map((test, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: getSubjectColor(test.subject), width: 40, height: 40 }}>
                      {test.student.name.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={500}>
                        {test.student.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Grade {test.student.grade} • {test.subject.charAt(0).toUpperCase() + test.subject.slice(1)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight={600} color={test.score >= 70 ? 'success.main' : 'error.main'}>
                        {test.score}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(test.endTime).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </StyledCard>
      </TabPanel>

      {/* Top Performers */}
      <TabPanel value={tabValue} index={3}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Top Performers
            </Typography>
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {stats.activity.topPerformers.map((performer, index) => (
                <Box key={index} sx={{ mb: 2, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{ 
                          bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'primary.main',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }}>
                      {performer.name.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight={600}>
                        {performer.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Grade {performer.grade}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        {performer.avgScore.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {performer.testCount} tests
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </StyledCard>
      </TabPanel>
    </Container>
  );
};

export default AdminAnalytics; 